import React, { useState } from "react";
import { X, Search, Tag, Check } from "lucide-react";

export default function POTagSearchModal({
  isOpen,
  onClose,
  tagSearchQuery,
  setTagSearchQuery,
  availableTags = [],
  availableBrandTags = [],
  selectedSearchTag,
  setSelectedSearchTag,
  vendors = [],
  onSelectVendor
}) {
  const [tagTypeFilter, setTagTypeFilter] = useState("all"); // "all" | "product" | "brand"

  if (!isOpen) return null;

  // Combine tags with their category types
  const allTagItems = [
    ...availableTags.map(tag => ({ name: tag, type: "product" })),
    ...availableBrandTags.map(brand => ({ name: brand, type: "brand" }))
  ];

  // Filter tag list based on search query and type filter
  const filteredTagItems = allTagItems.filter(item => {
    const matchesSearch = !tagSearchQuery || item.name.toLowerCase().includes(tagSearchQuery.toLowerCase());
    const matchesType = tagTypeFilter === "all" || item.type === tagTypeFilter;
    return matchesSearch && matchesType;
  });

  // Find vendors matching the selected tag (checked against both product tags and brand tags)
  const activeTaggedVendors = selectedSearchTag 
    ? vendors.filter(v => {
        const hasProductTag = v.tags && v.tags.includes(selectedSearchTag);
        const hasBrandTag = v.brandTags && v.brandTags.includes(selectedSearchTag);
        const isActive = v.status !== "Inactive" && v.status !== "Blacklist";
        return (hasProductTag || hasBrandTag) && isActive;
      })
    : [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ width: "90%", maxWidth: "850px", height: "82vh", display: "flex", flexDirection: "column" }}>
        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Search size={20} style={{ color: "var(--primary)" }} />
            <h3 style={{ margin: 0 }}>Find Vendors by Product & Brand Tag</h3>
          </div>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Search & Tag Type Filter Tabs */}
        <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: "220px" }}>
            <input
              type="text"
              className="form-input"
              placeholder="Search product or brand tags..."
              value={tagSearchQuery}
              onChange={(e) => setTagSearchQuery(e.target.value)}
              style={{ width: "100%", margin: 0 }}
            />
          </div>
          <div style={{ display: "flex", gap: "0.4rem", backgroundColor: "rgba(255, 255, 255, 0.05)", padding: "3px", borderRadius: "6px" }}>
            <button
              type="button"
              onClick={() => setTagTypeFilter("all")}
              style={{
                background: tagTypeFilter === "all" ? "var(--primary)" : "transparent",
                color: tagTypeFilter === "all" ? "#000000" : "var(--text-secondary)",
                border: "none",
                padding: "0.35rem 0.75rem",
                borderRadius: "4px",
                fontSize: "0.8rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              All ({allTagItems.length})
            </button>
            <button
              type="button"
              onClick={() => setTagTypeFilter("product")}
              style={{
                background: tagTypeFilter === "product" ? "var(--primary)" : "transparent",
                color: tagTypeFilter === "product" ? "#000000" : "var(--text-secondary)",
                border: "none",
                padding: "0.35rem 0.75rem",
                borderRadius: "4px",
                fontSize: "0.8rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              Product Tags ({availableTags.length})
            </button>
            <button
              type="button"
              onClick={() => setTagTypeFilter("brand")}
              style={{
                background: tagTypeFilter === "brand" ? "var(--primary)" : "transparent",
                color: tagTypeFilter === "brand" ? "#000000" : "var(--text-secondary)",
                border: "none",
                padding: "0.35rem 0.75rem",
                borderRadius: "4px",
                fontSize: "0.8rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              Brand Tags ({availableBrandTags.length})
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1.9fr", gap: "1.5rem", flex: 1, minHeight: 0 }}>
          {/* Left Column: Tags list */}
          <div style={{ borderRight: "1px solid var(--border-color)", paddingRight: "1rem", overflowY: "auto", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
            <h4 style={{ margin: "0 0 0.5rem 0", fontSize: "0.85rem", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Available Tags ({filteredTagItems.length})
            </h4>
            {filteredTagItems.map((item, idx) => {
              const isSelected = selectedSearchTag === item.name;
              const isBrand = item.type === "brand";
              return (
                <button
                  key={`${item.type}-${item.name}-${idx}`}
                  type="button"
                  onClick={() => setSelectedSearchTag(item.name)}
                  style={{
                    textAlign: "left",
                    padding: "0.5rem 0.75rem",
                    borderRadius: "6px",
                    backgroundColor: isSelected ? "var(--primary)" : "rgba(255, 255, 255, 0.02)",
                    color: isSelected ? "#000000" : "var(--text-primary)",
                    border: isSelected ? "1px solid var(--primary)" : "1px solid var(--border-color)",
                    cursor: "pointer",
                    fontWeight: isSelected ? "bold" : "normal",
                    transition: "all 0.15s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "0.5rem"
                  }}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</span>
                  <span
                    style={{
                      fontSize: "0.68rem",
                      padding: "1px 5px",
                      borderRadius: "4px",
                      backgroundColor: isSelected 
                        ? "rgba(0, 0, 0, 0.15)" 
                        : isBrand ? "rgba(16, 185, 129, 0.15)" : "rgba(33, 150, 243, 0.15)",
                      color: isSelected 
                        ? "#000000" 
                        : isBrand ? "#10b981" : "var(--primary)",
                      fontWeight: 700,
                      textTransform: "uppercase"
                    }}
                  >
                    {isBrand ? "Brand" : "Product"}
                  </span>
                </button>
              );
            })}
            {filteredTagItems.length === 0 && (
              <div style={{ color: "var(--text-muted)", fontSize: "0.85rem", fontStyle: "italic", padding: "1rem 0" }}>
                No matching tags found.
              </div>
            )}
          </div>

          {/* Right Column: Associated Vendors */}
          <div style={{ overflowY: "auto", paddingRight: "0.5rem" }}>
            {selectedSearchTag ? (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", borderBottom: "1px solid var(--border-color)", paddingBottom: "0.5rem" }}>
                  <h4 style={{ margin: 0, fontSize: "0.95rem" }}>
                    Vendors matching: <span style={{ color: "var(--primary)", fontWeight: "bold" }}>{selectedSearchTag}</span>
                  </h4>
                  <span style={{ fontSize: "0.8rem", color: "var(--text-secondary)", backgroundColor: "rgba(255, 255, 255, 0.05)", padding: "0.2rem 0.5rem", borderRadius: "10px" }}>
                    {activeTaggedVendors.length} found
                  </span>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
                  {activeTaggedVendors.map(vendor => (
                    <div
                      key={vendor.id}
                      onClick={() => {
                        if (onSelectVendor) {
                          onSelectVendor(vendor.name);
                          onClose();
                        }
                      }}
                      style={{
                        border: "1px solid var(--border-color)",
                        borderRadius: "8px",
                        padding: "0.85rem 1rem",
                        backgroundColor: "rgba(255, 255, 255, 0.01)",
                        cursor: onSelectVendor ? "pointer" : "default",
                        transition: "all 0.2s ease"
                      }}
                      onMouseEnter={(e) => {
                        if (onSelectVendor) {
                          e.currentTarget.style.borderColor = "var(--primary)";
                          e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.03)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (onSelectVendor) {
                          e.currentTarget.style.borderColor = "var(--border-color)";
                          e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.01)";
                        }
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.4rem", gap: "0.5rem" }}>
                        <div>
                          <h5 style={{ margin: 0, fontSize: "0.95rem", color: "var(--text-primary)", fontWeight: "bold" }}>{vendor.name}</h5>
                          {vendor.category && (
                            <span style={{ display: "inline-block", marginTop: "0.25rem", fontSize: "0.72rem", backgroundColor: "rgba(255, 255, 255, 0.05)", color: "var(--primary)", border: "1px solid var(--primary)", padding: "0.1rem 0.35rem", borderRadius: "4px" }}>
                              {vendor.category}
                            </span>
                          )}
                        </div>
                        {onSelectVendor && (
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectVendor(vendor.name);
                              onClose();
                            }}
                            style={{
                              padding: "0.3rem 0.75rem",
                              fontSize: "0.8rem",
                              minHeight: "auto",
                              height: "auto",
                              whiteSpace: "nowrap"
                            }}
                          >
                            Select Vendor
                          </button>
                        )}
                      </div>
                      
                      {/* Tags & Brands list for this vendor */}
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginTop: "0.4rem", marginBottom: "0.4rem" }}>
                        {vendor.tags && vendor.tags.map(t => (
                          <span key={t} className="sku-badge" style={{ fontSize: "0.7rem", background: "rgba(33, 150, 243, 0.15)", color: "var(--primary)", border: t === selectedSearchTag ? "1px solid var(--primary)" : "1px solid rgba(33, 150, 243, 0.3)" }}>
                            {t}
                          </span>
                        ))}
                        {vendor.brandTags && vendor.brandTags.map(b => (
                          <span key={b} className="sku-badge" style={{ fontSize: "0.7rem", background: "rgba(16, 185, 129, 0.15)", color: "#10b981", border: b === selectedSearchTag ? "1px solid #10b981" : "1px solid rgba(16, 185, 129, 0.3)" }}>
                            {b}
                          </span>
                        ))}
                      </div>

                      {(vendor.city || vendor.state) && (
                        <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)", marginBottom: "0.4rem" }}>
                          Location: {vendor.address ? `${vendor.address}, ` : ""}{vendor.city}, {vendor.state} {vendor.zip}
                        </div>
                      )}

                      {vendor.pocs && vendor.pocs.length > 0 && vendor.pocs[0].name && (
                        <div style={{ marginTop: "0.5rem", borderTop: "1px dashed var(--border-color)", paddingTop: "0.4rem" }}>
                          <div style={{ fontSize: "0.72rem", fontWeight: "bold", color: "var(--primary)", textTransform: "uppercase", marginBottom: "0.15rem" }}>Primary Contact</div>
                          <div style={{ fontSize: "0.8rem", color: "var(--text-primary)" }}>
                            <strong>{vendor.pocs[0].name}</strong> {vendor.pocs[0].title ? `(${vendor.pocs[0].title})` : ""}
                          </div>
                          <div style={{ fontSize: "0.78rem", color: "var(--text-secondary)", display: "flex", flexWrap: "wrap", gap: "0.75rem", marginTop: "0.1rem" }}>
                            {vendor.pocs[0].email && <span>Email: {vendor.pocs[0].email}</span>}
                            {vendor.pocs[0].phone && <span>Phone: {vendor.pocs[0].phone}</span>}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {activeTaggedVendors.length === 0 && (
                    <div style={{ color: "var(--text-muted)", fontSize: "0.85rem", fontStyle: "italic", textAlign: "center", padding: "2rem" }}>
                      No vendors are currently associated with this tag.
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--text-muted)", textAlign: "center", padding: "2rem" }}>
                <Search size={36} style={{ color: "var(--text-muted)", marginBottom: "1rem", opacity: 0.5 }} />
                <p style={{ margin: 0, fontSize: "0.95rem" }}>Select an available Product Tag or Brand Tag from the left column to view associated vendor partners.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

